import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/utils/cn'

interface ComboboxOption {
  value: string
  label: string
  disabled?: boolean
  description?: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value: string | null
  onValueChange: (value: string | null, option?: ComboboxOption) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  isLoading?: boolean
  className?: string
  onSearchChange?: (query: string) => void
  filterOptions?: boolean
  selectedLabel?: string | null
  /** Minimum popover width in px. Lets the dropdown be wider than a narrow trigger so long option labels stay readable. */
  minPopoverWidth?: number
  clearable?: boolean
  ariaLabel?: string
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  isLoading = false,
  className,
  onSearchChange,
  filterOptions = true,
  selectedLabel: selectedLabelProp,
  minPopoverWidth,
  clearable = true,
  ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (open && triggerRef.current) {
      // Small delay to ensure the popover is rendered
      const timer = setTimeout(() => {
        if (triggerRef.current) {
          setTriggerWidth(triggerRef.current.offsetWidth)
        }
      }, 0)
      return () => clearTimeout(timer)
    } else {
      setTriggerWidth(null)
    }
  }, [open])

  const filtered = React.useMemo(() => {
    if (!filterOptions) return options
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter(o => o.label.toLowerCase().includes(lower))
  }, [options, search, filterOptions])

  const selectedLabel = React.useMemo(() => {
    if (!value) return null
    return (
      options.find(o => o.value === value)?.label ?? selectedLabelProp ?? value
    )
  }, [options, value, selectedLabelProp])

  const handleSelect = (optionValue: string) => {
    const option = options.find(o => o.value === optionValue)
    if (option?.disabled) return
    onValueChange(optionValue === value ? null : optionValue, option)
    setOpen(false)
    setSearch('')
    onSearchChange?.('')
  }

  const popoverWidth =
    triggerWidth != null
      ? Math.max(triggerWidth, minPopoverWidth ?? 0)
      : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm ring-offset-background',
            'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
            className
          )}
        >
          <span
            className={cn(
              'truncate',
              !selectedLabel && 'text-muted-foreground'
            )}
          >
            {selectedLabel ?? placeholder}
          </span>
          <div className="flex items-center shrink-0 ml-1">
            {value && clearable && (
              <span
                role="button"
                tabIndex={0}
                className="rounded-sm p-0.5 hover:bg-muted cursor-pointer"
                onClick={e => {
                  e.stopPropagation()
                  onValueChange(null)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation()
                    onValueChange(null)
                  }
                }}
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="flex flex-col p-0"
        align="start"
        style={{ width: popoverWidth ? `${popoverWidth}px` : undefined }}
      >
        <div className="p-2">
          <input
            className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm outline-none placeholder:text-muted-foreground"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              onSearchChange?.(e.target.value)
            }}
          />
        </div>
        <div className="max-h-[min(15rem,var(--radix-popover-content-available-height,15rem))] overflow-y-auto px-1 pb-1">
          {isLoading && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          )}
          {!isLoading &&
            filtered.map(option => {
              const isSelected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  aria-disabled={option.disabled}
                  className={cn(
                    'relative flex w-full select-none items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                    option.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent/50'
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4" />}
                  </span>
                  <span className="truncate">{option.label}</span>
                  {option.description && (
                    <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </button>
              )
            })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
